import type {
  AeropayBankAccount,
  SelectBankAccount,
} from '@iheartjane/data-access';
import type { MarginProperties } from '@iheartjane/reefer';
import {
  Box,
  ConditionalWrapper,
  Flex,
  JaneLogo,
  JanePayLogo,
  Link,
  Typography,
  useTabletMediaQuery,
} from '@iheartjane/reefer';

import { BankCircledIcon } from './bankIcon';

export interface BankAccountDetailsProps extends MarginProperties {
  /** Bank account passed to the component for displaying its details and selected status if "selected" flag is on */
  bankAccount: AeropayBankAccount;
  /** When true, the Jane Pay logo will be replaced with "Powered by Jane" */
  hideJanePayBranding?: boolean;
  /** When true (along with hideJanePayBranding), the "Powered By Jane" logo will not be rendered */
  hidePoweredByJane?: boolean;
  limitLogoSize?: boolean;
  /** Function passed used for selecting a bank account */
  onSelect?: SelectBankAccount;
  /** Flag that indicates if the component should display which bank account is selected, and enable the selection of another one */
  selectable?: boolean;
}

/**
 * Display a user's Aeropay bank account details
 */
export const BankAccountDetails = ({
  bankAccount,
  limitLogoSize = false,
  hideJanePayBranding = false,
  hidePoweredByJane = false,
  onSelect = () => null,
  selectable = false,
  ...marginProps
}: BankAccountDetailsProps) => {
  const isMobile = useTabletMediaQuery({ width: 'max' });
  return (
    <Flex alignItems="center" {...marginProps} width="100%">
      <BankCircledIcon name={bankAccount.bank_name} />

      <ConditionalWrapper
        condition={bankAccount?.is_selected}
        wrapper={(children) => (
          <Flex
            flexDirection={isMobile ? 'column' : 'row'}
            alignItems={isMobile ? 'flex-start' : 'center'}
            width="100%"
          >
            {children}
          </Flex>
        )}
      >
        <Box ml={16}>
          {bankAccount.bank_name && (
            <Typography variant="body-bold" maxLines={2}>
              {bankAccount.bank_name}
            </Typography>
          )}

          {bankAccount.name && (
            <Typography variant="body" maxLines={2}>
              {bankAccount.name}
            </Typography>
          )}

          {selectable && (
            <Flex>
              {bankAccount.is_selected ? (
                <Typography variant="body">Primary account</Typography>
              ) : (
                <Link
                  onClick={() =>
                    onSelect({ bankAccountId: bankAccount.bank_account_id })
                  }
                >
                  Set as primary account
                </Link>
              )}{' '}
            </Flex>
          )}
        </Box>

        {bankAccount?.is_selected && !hideJanePayBranding && (
          <JanePayLogo
            mt={isMobile ? 4 : 0}
            ml={isMobile ? 16 : 'auto'}
            mr={isMobile ? 'auto' : 16}
            height="24px"
            style={limitLogoSize ? { maxWidth: 95, width: '100%' } : {}}
          />
        )}

        {bankAccount?.is_selected &&
          hideJanePayBranding &&
          !hidePoweredByJane && (
            <Flex
              alignItems="center"
              ml={isMobile ? 16 : 'auto'}
              mr={isMobile ? 'auto' : 16}
              mt={isMobile ? -4 : 0}
            >
              <Typography
                variant={isMobile ? 'mini-bold' : 'body'}
                mr={4}
                mt={isMobile ? 8 : 0}
              >
                Powered By
              </Typography>

              <JaneLogo
                removeSafeSpace
                variant="secondary"
                size={isMobile ? 'xs' : 'sm'}
              />
            </Flex>
          )}
      </ConditionalWrapper>
    </Flex>
  );
};
